<template>
  <div class="sv-toolbar" data-test="sv-toolbar">
    <button
      type="button"
      class="sv-toolbar__nav"
      aria-label="Previous day"
      data-test="sv-prev-day"
      @click="shift(-1)"
    >&#8249;</button>

    <!--
      Native <input type="date">, per the codebase convention — NOT b-form-datepicker. It gives the
      platform's own picker (including the one iPad users already know) for free.
    -->
    <label class="sv-toolbar__datelabel">
      <span class="sr-only">Schedule date</span>
      <input
        type="date"
        class="sv-toolbar__date"
        :value="date"
        data-test="sv-date"
        @change="$emit('change-date', $event.target.value)"
      />
    </label>

    <button
      type="button"
      class="sv-toolbar__nav"
      aria-label="Next day"
      data-test="sv-next-day"
      @click="shift(1)"
    >&#8250;</button>

    <button
      type="button"
      class="sv-toolbar__today"
      :disabled="isToday"
      data-test="sv-today"
      @click="$emit('change-date', today)"
    >Today</button>

    <span class="sv-toolbar__weekday" data-test="sv-weekday">{{ weekdayLabel }}</span>

    <span class="sv-toolbar__spacer"></span>

    <button
      type="button"
      class="sv-toolbar__refresh"
      :disabled="busy"
      data-test="sv-refresh"
      @click="$emit('refresh')"
    >{{ busy ? 'Refreshing…' : 'Refresh' }}</button>
  </div>
</template>

<script>
/**
 * Date navigation for the schedule.
 *
 * Day arithmetic is done on the YYYY-MM-DD string with moment's UTC parsing, never on a local
 * Date: `new Date('2026-09-19')` is parsed as UTC midnight and then rendered in the viewer's
 * timezone, so "yesterday" lands on the wrong day for anyone west of UTC. The date shown here is
 * the PRACTICE's day (see the store's todayForSite).
 */
import moment from 'moment-timezone';

export default {
  name: 'ScheduleToolbar',

  props: {
    /** YYYY-MM-DD, the day being viewed, in the site's timezone. */
    date: { type: String, required: true },
    /** YYYY-MM-DD for today in the site's timezone. */
    today: { type: String, required: true },
    busy: { type: Boolean, default: false },
  },

  computed: {
    isToday() {
      return this.date === this.today;
    },

    weekdayLabel() {
      const m = moment.utc(this.date, 'YYYY-MM-DD', true);
      return m.isValid() ? m.format('dddd, MMMM D') : '';
    },
  },

  methods: {
    shift(days) {
      const m = moment.utc(this.date, 'YYYY-MM-DD', true);
      if (!m.isValid()) return;
      this.$emit('change-date', m.add(days, 'day').format('YYYY-MM-DD'));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.sv-toolbar__nav {
  width: 32px;
  height: 34px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  color: $sv-ink;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:hover { border-color: $sv-brand; color: $sv-brand; }
}

.sv-toolbar__datelabel {
  margin: 0;
}

.sv-toolbar__date {
  // 16px in px, not rem: below 16px iOS Safari zooms in on focus and never zooms back out.
  font-size: 16px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  color: $sv-ink;

  &:focus { outline: none; border-color: $sv-brand; }
}

.sv-toolbar__today,
.sv-toolbar__refresh {
  height: 34px;
  padding: 0 12px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  color: $sv-ink;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { border-color: $sv-brand; color: $sv-brand; }
  &:disabled { opacity: 0.5; cursor: default; }
}

.sv-toolbar__weekday {
  font-size: 13px;
  font-weight: 600;
  color: $sv-ink-3;
}

.sv-toolbar__spacer {
  flex: 1 1 auto;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// Phone: the weekday shares the second row with Today and Refresh rather than taking a row of its
// own — on a phone every row of chrome pushes the first appointment further below the fold. It
// shrinks (with an ellipsis) before either button does.
@media (max-width: $sv-phone-max) {
  // The date flexes so ‹ date › always stay on one line together. At a fixed width, a 320px phone
  // wrapped the › button away from the date it steps.
  .sv-toolbar__datelabel {
    flex: 1 1 140px;
    min-width: 0;
  }

  .sv-toolbar__date {
    width: 100%;
    min-width: 0;
  }

  .sv-toolbar__weekday {
    // An 80px basis, not `auto`: flex decides where lines BREAK from the basis, so `auto` (the
    // full "Friday, September 25") pushed Refresh onto a third row at 320px instead of shrinking.
    // Not 0 either, or at 390px it would squeeze onto the date's row as "F…".
    flex: 1 1 80px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sv-toolbar__spacer { display: none; }
}
</style>
